import { MatrixRTCSessionEvent, } from 'matrix-js-sdk/lib/matrixrtc/MatrixRTCSession.js';
import { createDebugLogger } from './logger.js';
const debugLog = createDebugLogger('callKeyPipeline');
export const ownKeyWaitTimeoutMs = 10_000;
export const OWN_KEY_UNAVAILABLE_ERROR = 'Call own encryption key unavailable';
export const OWN_KEY_WAIT_CANCELLED_ERROR = 'Call own key wait cancelled';
/**
 * The one consumer of `EncryptionKeyChanged`, whatever transport carries the
 * media. It only ever runs for an encrypted room: MSC4143 makes MatrixRTC
 * encryption REQUIRED there and forbids it everywhere else.
 */
export const createCallKeyPipeline = () => {
    let rtcSession;
    const keys = new Map();
    let localIdentity = null;
    let localOutboundIdentity = null;
    let onKey;
    let waitResolve;
    let waitReject;
    let waitTimeout;
    const settleWait = (settle) => {
        waitResolve = undefined;
        waitReject = undefined;
        if (waitTimeout !== undefined)
            clearTimeout(waitTimeout);
        waitTimeout = undefined;
        settle();
    };
    const hasOwnKey = () => localOutboundIdentity !== null && keys.has(localOutboundIdentity);
    const maybeResolveOwnKey = () => {
        const resolve = waitResolve;
        if (resolve && hasOwnKey())
            settleWait(resolve);
    };
    const onEncryptionKeyChanged = (encryptionKey, encryptionKeyIndex, membershipParts, rtcBackendIdentity) => {
        debugLog.debug('call', `key changed identity=${rtcBackendIdentity} index=${encryptionKeyIndex} ownIdentity=${localOutboundIdentity ?? 'unset'}`);
        const own = membershipParts.userId === localIdentity?.userId &&
            membershipParts.deviceId === localIdentity?.deviceId;
        if (own)
            localOutboundIdentity = rtcBackendIdentity;
        // Every key is forwarded as it arrives. A key index is a slot in LiveKit's
        // per-participant key ring (`cryptoKeyRing[keyIndex % keyringSize]`) and the
        // sender writes it into the frame, so the receiver must simply hold whatever
        // the sender last put there. Filtering on the index would strand frames: the
        // SDK reuses indices modulo 256 and restarts at 0 whenever a peer rejoins.
        const entry = {
            identity: rtcBackendIdentity,
            keyIndex: encryptionKeyIndex,
            key: encryptionKey,
        };
        keys.set(rtcBackendIdentity, entry);
        onKey?.(entry, own);
        maybeResolveOwnKey();
    };
    const reset = () => {
        if (rtcSession) {
            rtcSession.off(MatrixRTCSessionEvent.EncryptionKeyChanged, onEncryptionKeyChanged);
            rtcSession = undefined;
        }
        keys.clear();
        localIdentity = null;
        localOutboundIdentity = null;
        const reject = waitReject;
        if (reject)
            settleWait(() => reject(new Error(OWN_KEY_WAIT_CANCELLED_ERROR)));
    };
    const detach = () => {
        reset();
        onKey = undefined;
    };
    // The listener survives a re-attach: `reemitEncryptionKeys` fires during the
    // attach, so a caller that set it first must still receive those keys.
    const attach = (session, identity) => {
        reset();
        rtcSession = session;
        localIdentity = identity;
        session.on(MatrixRTCSessionEvent.EncryptionKeyChanged, onEncryptionKeyChanged);
        session.reemitEncryptionKeys();
    };
    const waitForOwnKey = () => {
        if (hasOwnKey())
            return Promise.resolve();
        return new Promise((resolve, reject) => {
            waitResolve = resolve;
            waitReject = reject;
            waitTimeout = setTimeout(() => settleWait(() => reject(new Error(OWN_KEY_UNAVAILABLE_ERROR))), ownKeyWaitTimeoutMs);
            maybeResolveOwnKey();
        });
    };
    return {
        attach,
        detach,
        waitForOwnKey,
        getKeys: () => [...keys.values()],
        setOnKey: (listener) => {
            onKey = listener;
        },
    };
};
//# sourceMappingURL=callKeyPipeline.js.map